import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { fetchTivConversation, sendTivMessage } from '../api/tiv';
import TivFeed from '../components/tiv/TivFeed';
import ChatInput from '../components/ChatInput';
import TivProfilePanel from '../components/tiv/TivProfilePanel';

const CASPER_INIT = 'Hello, aide moi à écrire une lettre de motivation';

export default function TivChatPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [conversation, setConversation]   = useState(null);
  const [step,         setStep]           = useState(0);
  const [messages,     setMessages]       = useState([]);
  const [title,        setTitle]          = useState('');
  const [isWaiting,    setIsWaiting]      = useState(false);
  const [chatEnabled,  setChatEnabled]    = useState(false);
  const [showProfile,  setShowProfile]    = useState(false);
  const pendingCasperRef                  = useRef(false);
  const pendingRedirectRef                = useRef(false);
  const [focus,        setFocus]          = useState(0);
  const [loading,      setLoading]        = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const conv = await fetchTivConversation(id);
        setConversation(conv);
        setStep(conv.step);
        setTitle(conv.title || '');
        const msgs = conv.messages ?? [];
        setMessages(msgs);
        if (msgs.length > 0) setChatEnabled(true);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  // Une fois la page affichée, lancer le message casper si nouvelle conversation ou si pending
  useEffect(() => {
    if (loading) return;
    const pendingKey = `tiv_pending_casper_${id}`;
    const pendingMsg = localStorage.getItem(pendingKey);
    if (pendingMsg) {
      localStorage.removeItem(pendingKey);
      sendCasper(id, pendingMsg);
    } else if (messages.length === 0 && conversation) {
      sendCasperInit(id);
    }
  }, [loading]); // eslint-disable-line

  function applyBotMsg(botMsg, replace = false) {
    if (botMsg?.message?.content?.titre) setTitle(botMsg.message.content.titre);
    if (botMsg?.message?.content?.step) setStep(botMsg.message.content.step);
    setMessages(prev => replace ? [botMsg] : [...prev, botMsg]);

    const content = botMsg?.message?.content;
    if (
      (content?.step_signal === 'step_complete' && content?.step === 6) ||
      (content?.step_signal === 'end'           && content?.step === 8)
    ) {
      pendingRedirectRef.current = true;
    } else if (content?.step_signal === 'step_complete') {
      pendingCasperRef.current = true;
    }
  }

  async function sendCasper(convId, message) {
    setIsWaiting(true);
    try {
      const botMsg = await sendTivMessage(Number(convId), {
        conversation_id: Number(convId),
        message,
        from: 'casper',
      });
      applyBotMsg(botMsg);
    } catch (e) {
      console.error(e);
    } finally {
      setIsWaiting(false);
    }
  }

  async function sendCasperInit(convId) {
    setIsWaiting(true);
    try {
      const botMsg = await sendTivMessage(Number(convId), {
        conversation_id: Number(convId),
        message: CASPER_INIT,
        from: 'casper',
      });
      applyBotMsg(botMsg, true);
    } catch (e) {
      console.error(e);
    } finally {
      setIsWaiting(false);
    }
  }

  async function handleSend(text) {
    setChatEnabled(false);
    setFocus(f => f + 1);

    const optimistic = {
      id: `tmp_${Date.now()}`,
      sender: 'user',
      message: { sender: 'user', type: 'text', content: text },
      created_at: Date.now(),
    };
    setMessages(prev => [...prev, optimistic]);
    setIsWaiting(true);

    try {
      const botMsg = await sendTivMessage(Number(id), {
        conversation_id: Number(id),
        message: text,
        from: 'user',
      });
      applyBotMsg(botMsg);
    } catch (e) {
      console.error(e);
    } finally {
      setIsWaiting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <>
    <div className="flex flex-1 flex-col h-dvh bg-gray-900">
      {/* Header pleine largeur */}
      <div className="border-b border-white/[0.06] shrink-0">
        <div className="flex items-center gap-3 px-4 py-3 max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/tiv')}
            className="p-1 rounded-lg hover:bg-white/[0.05] transition-colors text-zinc-400"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-white truncate">
              {title || 'Nouvelle lettre'}
            </h1>
            <p className="text-xs text-zinc-500">Lettre de motivation · Tiv</p>
          </div>
          <button
            onClick={() => setShowProfile(true)}
            className="px-3 py-1.5 rounded-lg bg-violet-500/15 border border-violet-500/30 text-violet-400 text-xs font-medium hover:bg-violet-500/20 transition-colors shrink-0"
          >
            Profil Tiv
          </button>
          {conversation.step >= 2 && (
            <button
              onClick={() => navigate(`/tiv/lettre-motivation/${id}/detail`)}
              className="px-3 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.09] transition-colors text-zinc-300 text-xs font-medium shrink-0"
            >
              Ma lettre
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden max-w-4xl w-full mx-auto">
        {/* Feed */}
        <TivFeed
          messages={messages}
          isWaiting={isWaiting}
          onSend={handleSend}
          onTypingDone={() => {
            if (pendingRedirectRef.current) {
              pendingRedirectRef.current = false;
              setTimeout(() => navigate(`/tiv/lettre-motivation/${id}/detail`), 1000);
            } else if (pendingCasperRef.current) {
              pendingCasperRef.current = false;
              sendCasper(id, 'ok on continue');
            } else {
              setChatEnabled(true);
            }
          }}
        />

        {/* Input */}
        <div className="px-4 pb-4 pt-2 shrink-0">
          <ChatInput
            placeholder="Réponds à Tiv…"
            onSend={handleSend}
            focus={focus}
            chatEnabled={chatEnabled && !isWaiting}
          />
        </div>
      </div>
    </div>
    <TivProfilePanel open={showProfile} onClose={() => setShowProfile(false)} />
    </>
  );
}
